import {
	addCustomData,
	addCustomTiming,
	addMark,
	getActiveInteraction,
} from '@atlaskit/react-ufo/interaction-metrics';

import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/page-segment-load';

import { BM3_MIGRATION_SCOPES } from './bm3MigrationConfig.ts';
import { PAGE_SEGMENT_MARK_PREFIX_END } from './metric-keys/index.ts';

type SegmentMetric = Pick<PageSegmentLoadMetric, 'key'>;

type UFOBinding = {
	interactionId: string;
	key: string;
	startTime: number;
};

// Dispatch information only: UFO still owns holds, completion and reporting.
// Object identity keeps separate metric instances with the same key independent.
const bindings = new WeakMap<SegmentMetric, UFOBinding>();
// Deduplicate end telemetry per captured start without changing UFO completion or holds.
const reportedEnds = new WeakSet<UFOBinding>();

export function bindPageSegmentToUFO(
	metric: SegmentMetric,
	key: string = metric.key,
	startTime?: number | 'page-load',
): UFOBinding | undefined {
	bindings.delete(metric);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- Browser telemetry binding only; this does not affect rendered content.
	if (process.env.REACT_SSR) {
		return undefined;
	}
	const interaction = getActiveInteraction();
	if (
		interaction &&
		BM3_MIGRATION_SCOPES.some(
			({ ufoNames, segmentKeys }) =>
				ufoNames.includes(interaction.ufoName) && segmentKeys.some((segment) => segment === key),
		)
	) {
		const binding = {
			interactionId: interaction.id,
			key,
			startTime: startTime === 'page-load' ? interaction.start : (startTime ?? performance.now()),
		};
		bindings.set(metric, binding);
		return binding;
	}
	return undefined;
}

export function getPageSegmentUFOBinding(metric: SegmentMetric): UFOBinding | undefined {
	return bindings.get(metric);
}

export function reportPageSegmentEndToUFO(
	binding: UFOBinding,
	options?: { stopTime?: number; customData?: Parameters<typeof addCustomData>[2] | null },
): void {
	const { interactionId, key, startTime } = binding;
	if (!reportedEnds.has(binding)) {
		reportedEnds.add(binding);
		const endTime = options?.stopTime ?? performance.now();
		addMark(interactionId, 'custom', `${PAGE_SEGMENT_MARK_PREFIX_END}:${key}`, [], endTime);
		addCustomTiming(interactionId, [], { [`wf/react/${key}`]: { startTime, endTime } });
	}
	// Later React/imperative callbacks can still supply attributes to the same owner.
	if (options?.customData) {
		addCustomData(
			interactionId,
			[],
			typeof globalThis.structuredClone === 'function'
				? globalThis.structuredClone(options.customData)
				: options.customData,
		);
	}
}
